import { createEntityAdapter, createSlice } from '@reduxjs/toolkit'
import api from 'app/services/api'
import { clearFilters, selectionChanged, termSearchChanged } from 'app/thunks'
import {
  buildTermTree,
  distinctArray,
  getTermAncestors,
  isTaxonomyId,
  removeFromDistinctArray,
  sanitizeTermQueryResults,
  validateSelections,
} from 'helpers'
import type {
  GetTaxonomyTermsQueryFulfilled,
  TaxonomyConfig,
  TaxonomyId,
  TaxSliceExtraReducersCallback,
  TaxSliceState,
  Term,
  TermList,
} from 'types'

export const matchTermsCallback = <T extends TaxonomyId>(
  x: any,
  taxonomyId: T
): x is GetTaxonomyTermsQueryFulfilled<T> => {
  if (!api.endpoints.getTerms.matchFulfilled(x)) return false
  return x.meta.arg.originalArgs.taxonomy === taxonomyId
}

export function createTaxSlice<T extends TaxonomyId>(
  config: TaxonomyConfig<T> | T,
  extraReducersCallback?: TaxSliceExtraReducersCallback
) {
  const parsedConfig: TaxonomyConfig<T> = isTaxonomyId(config)
    ? { taxonomyId: config }
    : config

  const taxonomyId: T = parsedConfig.taxonomyId

  const termsAdapter = taxonomyId !== 'PRODUCTCATEGORY' ? createEntityAdapter<Term>({
    selectId: (item) => item.id,
    sortComparer: (a, b) => a.name.localeCompare(b.name),
  }) : createEntityAdapter<Term>({
    selectId: (item) => item.id,
  });

  const initialState: TaxSliceState<T> = {
    label: `${taxonomyId}`,
    allowNull: true,
    hierarchical: true,
    exclusive: false,
    ...parsedConfig,
    termsLoaded: false,
    termTree: [],
    selections: [],
    filtered: [],
    status: 'idle',
  }

  return createSlice({
    name: taxonomyId,
    initialState: termsAdapter.getInitialState(initialState),
    reducers: {},
    extraReducers: (builder) => {
      builder.addCase(clearFilters.pending, (state) => {
        if (state.exclusive) return
        state.selections = validateSelections({ ...state, selections: [] })
      })

      builder
        // Perform the term search
        .addCase(termSearchChanged.pending, (state, action) => {
          const { taxonomyId, search } = action.meta.arg
          const searchLowerCase = search.toLowerCase()
          // Bail early if not this taxonomy
          if (taxonomyId !== state.taxonomyId) return

          // Store the current value
          const prevSearchLowerCase = (state.search || '').toLowerCase()
          // Update the search value
          state.search = search

          if (!search.length) {
            state.filtered = []
            return
          }

          const { selectById, selectIds, selectEntities } =
            termsAdapter.getSelectors()
          const allTerms = selectEntities(state)

          // If search is just more refined, only search among existing results
          const searchExisting =
            prevSearchLowerCase.length > 0 &&
            searchLowerCase.includes(prevSearchLowerCase)

          // Filter down the IDs - but include the ancestors of any matches
          const termIds = (
            searchExisting
              ? state.filtered || []
              : (selectIds(state) as TermList)
          ).reduce((results, termId) => {
            const term = selectById(state, termId)
            if (!term || !term.name.toLowerCase().includes(searchLowerCase))
              return results
            return [...results, ...getTermAncestors(termId, allTerms)]
          }, [] as TermList)

          state.filtered = distinctArray(termIds)
        })

      builder.addCase(selectionChanged.pending, (state, action) => {
        const { taxonomyId, termId, checked } = action.meta.arg

        if (taxonomyId !== state.taxonomyId) return

        const { selectEntities } = termsAdapter.getSelectors()

        state.status = 'idle'

        const { exclusive } = state
        let selections: TermList = [...state.selections]

        const termWithAncestors = getTermAncestors(
          termId,
          selectEntities(state)
        )

        // Only one term checked at a time
        if (exclusive && checked) selections = termWithAncestors

        // Add/remove term(s)
        if (checked) {
          selections = distinctArray([...selections, ...termWithAncestors])
        } else {
          selections = removeFromDistinctArray(selections, termId)
        }

        // Ensure selections is valid
        selections = validateSelections({ ...state, selections })

        state.selections = selections
      })

      builder.addMatcher(
        (x) => matchTermsCallback(x, taxonomyId),
        (state, { payload }) => {
          let terms = sanitizeTermQueryResults(payload)

          const { boolean, booleanLabel } = state

          terms = !boolean
            ? terms
            : terms.reduce((result, term) => {
                if (term.name.toUpperCase() !== 'YES') return result
                const name = booleanLabel ? booleanLabel : term.name

                return [{ ...term, name }]
              }, [] as Term[])

          termsAdapter.addMany(state, terms)

          state.status = 'idle'
          state.termsLoaded = true
          state.termTree = buildTermTree(state, termsAdapter)
          state.selections = validateSelections(state)
        }
      )

      extraReducersCallback && extraReducersCallback(builder, taxonomyId)
    },
  })
}

export default createTaxSlice
